<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useQuasar } from 'quasar'
import { GoogleMap, AdvancedMarker, Polygon } from 'vue3-google-map'
import apiAddress from '../../api/address.api'
import apiDevice from '../../api/device.api'
import DeviceDialog from '../board/address/DeviceDialog.vue'

const $q = useQuasar()

const loading = ref(false)
const searchQuery = ref('')
const addresses = ref([])
const devices = ref([])

const mapRef = ref(null)
const selectedDevice = ref(null)
const deviceDialogOpen = ref(false)
const mapCenter = ref({ lat: 47.921245205749806, lng: 106.91047711004293 })
const currentMapZoom = ref(14)
const ZOOM_LEVEL_POLYGON_DISPLAY = 17

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
const CLOUD_MAP_ID = computed(() =>
  $q.dark.isActive
    ? import.meta.env.VITE_CLOUD_MAP_ID_DARK
    : import.meta.env.VITE_CLOUD_MAP_ID_LIGHT
)

const isValidLatLng = (lat, lng) => {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    isFinite(lat) &&
    isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  )
}

const getAddressCenter = (address) => {
  if (!Array.isArray(address?.locationPolygon) || address.locationPolygon.length === 0) {
    return null
  }

  const validPoints = address.locationPolygon.filter((point) => {
    if (!Array.isArray(point) || point.length < 2) return false
    const [lng, lat] = point
    return isValidLatLng(lat, lng)
  })

  if (!validPoints.length) return null

  const { latSum, lngSum } = validPoints.reduce(
    (acc, [lng, lat]) => ({
      latSum: acc.latSum + lat,
      lngSum: acc.lngSum + lng,
    }),
    { latSum: 0, lngSum: 0 }
  )

  return {
    lat: latSum / validPoints.length,
    lng: lngSum / validPoints.length,
  }
}

const getAddressPolygonPath = (address) => {
  if (!Array.isArray(address?.locationPolygon) || address.locationPolygon.length < 3) {
    return []
  }

  return address.locationPolygon
    .map((point) => {
      if (!Array.isArray(point) || point.length < 2) return null
      const [lng, lat] = point
      if (!isValidLatLng(lat, lng)) return null

      return { lat, lng }
    })
    .filter(Boolean)
}

const filteredAddresses = computed(() => {
  if (!searchQuery.value?.trim()) return addresses.value

  const query = searchQuery.value.toLowerCase()
  return addresses.value.filter((address) =>
    [
      address.name,
      address.city,
      address.district,
      address.subDistrict,
      address.street,
      address.building,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query))
  )
})

const filteredAddressIds = computed(() =>
  new Set(filteredAddresses.value.map((address) => String(address._id)))
)

const mappedAddresses = computed(() =>
  filteredAddresses.value
    .map((address) => {
      const center = getAddressCenter(address)
      const polygonPath = getAddressPolygonPath(address)
      if (!center || polygonPath.length < 3) return null

      return {
        ...address,
        _center: center,
        _polygonPath: polygonPath,
      }
    })
    .filter(Boolean)
)

const mappedDevices = computed(() =>
  devices.value
    .filter((device) => {
      if (!Array.isArray(device?.location) || device.location.length < 2) return false

      const [lng, lat] = device.location
      if (!isValidLatLng(lat, lng)) return false

      if (!searchQuery.value?.trim()) return true

      const matchesDevice = [device.name, device.deviceId, device.deviceNumber]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchQuery.value.toLowerCase()))

      const matchesAddress = filteredAddressIds.value.has(String(device.addressOId))

      return matchesDevice || matchesAddress
    })
    .map((device) => ({
      ...device,
      _position: {
        lat: device.location[1],
        lng: device.location[0],
      },
    }))
)

function getAddressPolygonOptions(address) {
  return {
    paths: address._polygonPath,
    strokeColor: '#9a07bfff',
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: '#9a07bfff',
    fillOpacity: $q.dark.isActive ? 0.28 : 0.18,
  }
}

function getAddressMarkerOptions(address) {
  const markerContent = document.createElement('div')
  markerContent.style.cssText = `
    background: ${$q.dark.isActive ? '#1e1e1e' : '#ffffff'};
    color: ${$q.dark.isActive ? '#ffffff' : '#000000'};
    border: 2px solid #9a07bfff;
    border-radius: 8px;
    padding: 8px 10px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  `
  markerContent.textContent = address.name || `${address.district || ''} ${address.street || ''}`.trim() || 'Address'

  return {
    position: address._center,
    content: markerContent,
  }
}

function getDeviceMarkerOptions(device) {
  const isOnline = device.isOnline === true
  const activeColor = isOnline ? '#00c853' : '#9e9e9e'
  const activeColorAlpha = isOnline ? 'rgba(0, 200, 83, 0.7)' : 'rgba(158, 158, 158, 0.7)'

  const markerContent = document.createElement('div')
  markerContent.style.cssText = `
    position: relative;
    width: 88px;
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  `

  const rippleOne = document.createElement('span')
  rippleOne.style.cssText = `
    position: absolute;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    border: 3px solid ${activeColorAlpha};
    ${isOnline ? 'animation: device-pulse-wave 2.2s ease-out infinite;' : ''}
    transform: scale(0.5);
    opacity: 0;
  `

  const rippleTwo = document.createElement('span')
  rippleTwo.style.cssText = `
    position: absolute;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    border: 3px solid ${activeColorAlpha};
    ${isOnline ? 'animation: device-pulse-wave 2.2s ease-out infinite; animation-delay: 1.1s;' : ''}
    transform: scale(0.5);
    opacity: 0;
  `

  const core = document.createElement('span')
  core.style.cssText = `
    position: absolute;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    background: ${activeColor};
    border: 3px solid #ffffff;
    box-shadow: 0 0 14px ${activeColorAlpha};
  `
  core.title = device.deviceNumber || device.deviceId || 'Device'

  markerContent.appendChild(rippleOne)
  markerContent.appendChild(rippleTwo)
  markerContent.appendChild(core)

  return {
    position: device._position,
    content: markerContent,
    anchorLeft: '-50%',
    anchorTop: '-50%',
    zIndex: 2000,
  }
}

const onDeviceMarkerClick = (device) => {
  selectedDevice.value = device
  deviceDialogOpen.value = true
}

async function fetchAddresses(skipBoundsAdjustment = false) {
  try {
    loading.value = true

    const [{ data: addressData }] = await Promise.all([
      apiAddress.getList({
        page: 1,
        'page-size': 100000,
      }),
    ])

    const deviceData = addressData.reduce((acc, address) => {
      if (Array.isArray(address?.devices)) {
        const devicesWithAddress = address.devices.map((device) => ({
          ...device,
          addressOId: address._id,
          address: address,
        }))
        return acc.concat(devicesWithAddress)
      }
      return acc
    }, [])

    addresses.value = Array.isArray(addressData) ? addressData : []
    devices.value = Array.isArray(deviceData) ? deviceData : []

    if (!skipBoundsAdjustment && mapRef.value?.map) {
      const bounds = new google.maps.LatLngBounds()
      let hasBounds = false

      addresses.value.forEach((address) => {
        const center = getAddressCenter(address)
        if (!center) return
        bounds.extend(center)
        hasBounds = true
      })

      if (hasBounds) {
        mapRef.value.map.fitBounds(bounds, {
          padding: { top: 80, right: 50, bottom: 50, left: 50 },
        })
      }
    }
  } catch (error) {
    console.error('Error fetching addresses:', error)
  } finally {
    loading.value = false
  }
}

const onMarkerClick = (address) => {
  if (!mapRef.value?.map || !address?._center) return

  mapRef.value.map.setCenter(address._center)
  mapRef.value.map.setZoom(18)
  mapCenter.value = address._center
}

const onZoomChanged = () => {
  if (!mapRef.value?.map) return
  currentMapZoom.value = mapRef.value.map.getZoom()
}

onMounted(() => {
  if (!window.google || !window.google.maps) {
    console.error('Google Maps API not loaded')
    return
  }

  fetchAddresses()
})

watch(() => $q.dark.isActive, () => {
  setTimeout(() => {
    fetchAddresses(true)
  }, 300)
})
</script>

<template lang="pug">
.map-container(style="position: relative; width: 100%; height: 95vh;")
  .search-container(style="position: absolute; top: 10px; left: 50%; transform: translateX(-50%); z-index: 1000; width: 90%; max-width: 400px;")
    .row.q-gutter-sm
      .col
        q-input(
          v-model="searchQuery"
          outlined
          dense
          :class=`$q.dark.isActive ? 'bg-grey-10' : 'bg-grey-4'`
          placeholder="Search address"
          clearable
        )
          template(v-slot:prepend)
            q-icon(name="search")
      .col-auto
        q-btn(
          icon="refresh"
          color="primary"
          dense
          :loading="loading"
          @click="fetchAddresses"
        )
          q-tooltip Refresh

  component(
    :is="GoogleMap"
    ref="mapRef"
    :key="`map-${$q.dark.isActive}`"
    :api-key="GOOGLE_MAPS_API_KEY"
    :mapId="CLOUD_MAP_ID"
    style="width: 100%; height: 100%; min-height: 400px;"
    :center="mapCenter"
    :zoom="currentMapZoom"
    :streetViewControl="false"
    :disableDefaultUI="false"
    gestureHandling="greedy"
    @zoom_changed="onZoomChanged"
  )
    template(v-for="address in mappedAddresses" :key="address._id")
      component(
        :is="AdvancedMarker"
        v-if="currentMapZoom < ZOOM_LEVEL_POLYGON_DISPLAY"
        :options="getAddressMarkerOptions(address)"
        @click="onMarkerClick(address)"
      )
      component(
        :is="Polygon"
        v-else
        :options="getAddressPolygonOptions(address)"
        @click="onMarkerClick(address)"
      )

    template(v-if="currentMapZoom >= ZOOM_LEVEL_POLYGON_DISPLAY")
      component(
        :is="AdvancedMarker"
        v-for="device in mappedDevices"
        :key="`device-${device._id}`"
        :options="getDeviceMarkerOptions(device)"
        @click="onDeviceMarkerClick(device)"
      )

  component(
    :is="DeviceDialog"
    v-model="deviceDialogOpen"
    :device="selectedDevice"
    :address="selectedDevice?.address"
  )
</template>

<style>
@keyframes device-pulse-wave {
  0% {
    transform: scale(0.4);
    opacity: 0.9;
  }

  70% {
    opacity: 0.25;
  }

  100% {
    transform: scale(4.4);
    opacity: 0;
  }
}
</style>
